import { parseDate } from '@/core/lib/date';
import type { ImagePath } from '@/core/lib/imageHelper';
import { getTeamImage } from '@/core/lib/teamLogoHelper';
import { toInteger, toObject } from '@/core/oddsApi/helpers/toggleHelper';
import { DisplaySection, EventStatus, TimezoneOffset, TournamentStage } from '@/interface/enum';
import type { IKnockoutEvent, IKnockoutStage, ISboEvent, ITournamentCenterStageConfig, ITournamentEvent } from '@/interface/tournamentCenter';
import { KnockoutEventStatus } from '@/interface/tournamentCenter';

export function getKnockoutStagesForSlides(knockoutStages: IKnockoutStage[]): TournamentStage[] {
    return knockoutStages
        .filter(ks => ks.stage !== TournamentStage.ThirdPlace)
        .map(ks => ks.stage);
}

export function getKnockoutRunningEvent(event: IKnockoutEvent, runningEvents: ITournamentEvent[]): ITournamentEvent | null {
    const eventIds = [
        event.regularEvent?.id,
        event.extraTimeEvent?.id,
        event.penaltyEvent?.id,
    ].filter((id): id is number => id !== undefined && id !== null);

    return eventIds
        .map(eventId => runningEvents.find(event => event.id === eventId))
        .find(event => event !== undefined) ?? null;
}

function hasRunningEvent(event: IKnockoutEvent, runningEvents: ITournamentEvent[]): boolean {
    return getKnockoutRunningEvent(event, runningEvents) !== null;
}

function isEventTbd(event: IKnockoutEvent): boolean {
    return !event.regularEvent;
}

function isEventCompleted(event: ISboEvent): boolean {
    return event?.status === EventStatus.Completed;
}

function hasIncompleteEvent(event: IKnockoutEvent): boolean {
    const eventTypes = [
        event.regularEvent,
        event.extraTimeEvent,
        event.penaltyEvent,
    ];

    return eventTypes.some(eventType => eventType !== null && !isEventCompleted(eventType));
}

export function getKnockoutEventStatus(
    event: IKnockoutEvent,
    runningEvents: ITournamentEvent[],
): KnockoutEventStatus {
    if (hasRunningEvent(event, runningEvents)) {
        return KnockoutEventStatus.Running;
    }

    if (isEventTbd(event)) {
        return KnockoutEventStatus.Tbd;
    }

    if (hasIncompleteEvent(event)) {
        return KnockoutEventStatus.Closed;
    }

    return KnockoutEventStatus.End;
}

const flagRoundedImageGlob = import.meta.glob(
    '@/assets/images/tournamentCenter/worldCup2026/flagRounded/*.{png,webp}',
    { eager: true, query: '?url' },
);

const footballPlayerImageGlob = import.meta.glob(
    '@/assets/images/tournamentCenter/worldCup2026/footballPlayer/*.{png,webp}',
    { eager: true, query: '?url' },
);

export function getTournamentCenterTeamImage(name: string | null | undefined): ImagePath {
    return getTeamImage(flagRoundedImageGlob, name);
}

export function getTournamentCenterPlayerImage(name: string | null | undefined): ImagePath {
    return getTeamImage(footballPlayerImageGlob, name);
}

export function formatTournamentCenterStageStartDateByLeagueConfig(value?: string): Map<number, Map<TournamentStage, Date>> {
    try {
        const config = toObject<ITournamentCenterStageConfig>(value, {});

        const result = new Map<number, Map<TournamentStage, Date>>();
        if (!config) return result;

        Object.entries(config).forEach(([leagueIdStr, stageDates]) => {
            const leagueId = toInteger(leagueIdStr);
            if (leagueId === 0) return;

            const stageMap = new Map<TournamentStage, Date>();

            Object.entries(stageDates).forEach(([stageKey, dateString]) => {
                const stage = toInteger(stageKey);
                if (stage === TournamentStage.Unknown) return;

                const startDate = parseDate(dateString, "yyyy-MM-dd'T'HH:mm:ss", TimezoneOffset.Singapore);
                stageMap.set(stage, startDate);
            });

            if (stageMap.size > 0) {
                result.set(leagueId, stageMap);
            }
        });

        return result;
    } catch (error) {
        console.error('Failed to format tournament center stage start date by league config:', error);
        return new Map();
    }
}

export function getKnockoutCurrentStage(stageConfigByLeague: Map<number, Map<TournamentStage, Date>>, leagueId: number): TournamentStage {
    const leagueConfig = stageConfigByLeague.get(leagueId);
    if (!leagueConfig || leagueConfig.size === 0) {
        return TournamentStage.Unknown;
    }

    const stages = Array.from(leagueConfig.entries());
    const sortedStages = [...stages].sort((a, b) => a[1].getTime() - b[1].getTime());

    const now = new Date();
    if (now < sortedStages[0][1]) {
        return TournamentStage.Group;
    }

    const currentStage = sortedStages
        .filter(([, startDate]) => now >= startDate)
        .pop();

    if (!currentStage) {
        return TournamentStage.Group;
    }

    const stage = currentStage[0];
    if (stage === TournamentStage.ThirdPlace) {
        return TournamentStage.Final;
    }

    return stage;
}

type TournamentCenterOddsGtmLabel = {
    clickGtmLabel: string;
    unclickGtmLabel: string;
};

const gtmConfig: Partial<Record<DisplaySection, TournamentCenterOddsGtmLabel>> = {
    [DisplaySection.TournamentCenter_Groups]: {
        clickGtmLabel: 'Groups_Odds',
        unclickGtmLabel: 'Groups_Odds_Unclick',
    },
    [DisplaySection.TournamentCenter_Knockout]: {
        clickGtmLabel: 'Knockouts_Odds',
        unclickGtmLabel: 'Knockouts_Odds_Unclick',
    },
    [DisplaySection.TournamentCenter_UpcomingMatches]: {
        clickGtmLabel: 'UpcomingMatches_Odds',
        unclickGtmLabel: 'UpcomingMatches_Odds_Unclick',
    },
    [DisplaySection.TournamentCenter_Country]: {
        clickGtmLabel: 'Country_Odds',
        unclickGtmLabel: 'Country_Odds_Unclick',
    },
    [DisplaySection.TournamentCenter_Matches]: {
        clickGtmLabel: 'Matches_Odds',
        unclickGtmLabel: 'Matches_Odds_Unclick',
    },
    [DisplaySection.TournamentCenter_FeaturedMatches]: {
        clickGtmLabel: 'FeaturedMatches_Odds',
        unclickGtmLabel: 'FeaturedMatches_Odds_Unclick',
    },
};

export function getTournamentCenterOddsGtmLabel(displaySection: DisplaySection): TournamentCenterOddsGtmLabel {
    return {
        clickGtmLabel: gtmConfig[displaySection]?.clickGtmLabel ?? '',
        unclickGtmLabel: gtmConfig[displaySection]?.unclickGtmLabel ?? '',
    };
}
